// src/components/home/ExamPaperPicker.jsx
//
// Homepage row 2: "See the real paper." Three steps (exam, level, sign up) across
// the top; below them the chosen exam's real booklet: cover plus two task pages,
// screenshotted from the app's own paper mode (public/paper-preview/, made by
// scripts/capture-paper-previews.mjs from each exam's FREE paper), and a deck card
// with the number of further papers. Every exam and level shows its own booklet,
// so switching from telc B2 to telc C1 really shows the C1 Hochschule layout.
//
// Clicking a page opens it full size in a dialog, so a visitor can read a real
// task before signing up. The choice travels into sign-up (onSignup), where the
// page saves it for onboarding.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { getMockExam } from '../../data/mockExamCatalog';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.025em' };

const PROVIDERS = [
  { id: 'telc', name: 'telc' },
  { id: 'goethe', name: 'Goethe' },
  { id: 'osd', name: 'ÖSD' },
];
const LEVELS = ['B1', 'B2', 'C1'];

/** What each preview page shows; order matches PAGES in capture-paper-previews.mjs. */
const PAGE_LABELS = {
  'telc-b1': ['Cover', 'Leseverstehen, Teil 1: Überschriften', 'Leseverstehen, Teil 1: Texte'],
  'telc-b2': ['Cover', 'Leseverstehen, Teil 1: Überschriften', 'Leseverstehen, Teil 1: Texte'],
  'telc-c1': ['Cover', 'Leseverstehen, Teil 1: Textrekonstruktion', 'Leseverstehen, Teil 2'],
  'goethe-b1': ['Cover', 'Lesen, Teil 1: Blog', 'Lesen, Teil 1: Richtig oder Falsch'],
  'goethe-b2': ['Cover', 'Lesen, Teil 1: Forum', 'Lesen, Teil 1: Aussagen'],
  'goethe-c1': ['Cover', 'Lesen, Teil 1: Fachartikel', 'Lesen, Teil 2: Artikel'],
  'osd-b1': ['Cover', 'Lesen, Aufgabe 1: Überschriften', 'Lesen, Aufgabe 2: Zeitungsartikel'],
  'osd-b2': ['Cover', 'Lesen, Aufgabe 1: Zeitungsartikel', 'Sprachbausteine: der abgeschnittene Rand'],
  'osd-c1': ['Cover', 'Lesen, Aufgabe 1: Magazinartikel', 'Lesen, Aufgabe 2: Überschriften'],
};

/** Desktop resting layout: wide, slightly overlapping, left to right. */
const REST = [
  { x: 0, y: 26, r: -3, z: 3 },
  { x: 268, y: 12, r: 1.5, z: 2 },
  { x: 536, y: 30, r: 3.5, z: 1 },
];
const DECK_X = 880;

const pageSrc = (slug, i) => `/paper-preview/${slug}-${i}.webp`;

function StepLabel({ n, children }) {
  return (
    <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-stone-500">
      <span className="text-stone-400 mr-1">{n}</span>
      {children}
    </span>
  );
}

function Chip({ on, onClick, children, level = false }) {
  const base = 'min-h-[44px] px-3.5 rounded-full text-[14px] cursor-pointer transition-colors duration-200 active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-900';
  const look = level
    ? on ? 'bg-[#E6F2F3] text-[#00525A] border-2 border-[#006D77] font-bold' : 'bg-white text-stone-700 border border-stone-200 hover:border-stone-400 font-bold'
    : on ? 'bg-stone-900 text-white border border-stone-900 font-semibold' : 'bg-white text-stone-900 border border-stone-200 hover:border-stone-400 font-semibold';
  return (
    <button type="button" aria-pressed={on} onClick={onClick} className={`${base} ${look} ${level ? 'min-w-[52px]' : ''}`} style={level ? DISPLAY : undefined}>
      {children}
    </button>
  );
}

/**
 * Full-size page viewer. A real dialog: focus moves in, Escape and arrows work.
 * Portalled to <body>: the homepage wraps sections in transformed elements, and a
 * transformed ancestor would trap position: fixed inside the section.
 */
function PageDialog({ slug, examName, index, onClose, onStep, t }) {
  const closeRef = useRef(null);
  const labels = PAGE_LABELS[slug];
  useEffect(() => {
    const prev = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onStep(1);
      if (e.key === 'ArrowLeft') onStep(-1);
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [onClose, onStep]);
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${examName}: ${labels[index]}`}
      className="fixed inset-0 z-[100] bg-stone-950/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3 p-3 sm:p-6"
    >
      <div className="w-full max-w-xl flex items-center justify-between gap-3 text-white">
        <div className="min-w-0">
          <p className="text-[15px] font-bold truncate" style={DISPLAY}>{labels[index]}</p>
          <p className="text-[12px] text-stone-300 truncate">
            {t('homeB.paperPicker.dialogSource', { examName, defaultValue: '{{examName}} · Practice paper 1, as it looks in paper mode' })}
          </p>
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={t('homeB.paperPicker.close', 'Close')}
          className="shrink-0 w-11 h-11 rounded-full border border-stone-600 inline-flex items-center justify-center cursor-pointer hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <img
        src={pageSrc(slug, index)}
        alt={`${examName}, ${labels[index]}`}
        className="max-h-[82svh] max-w-full rounded shadow-2xl bg-white"
      />
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onStep(-1)} aria-label={t('homeB.paperPicker.prev', 'Previous page')} className="w-11 h-11 rounded-full border border-stone-600 text-white inline-flex items-center justify-center cursor-pointer hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-[13px] text-stone-300 tabular-nums">{index + 1} / {labels.length}</span>
        <button type="button" onClick={() => onStep(1)} aria-label={t('homeB.paperPicker.next', 'Next page')} className="w-11 h-11 rounded-full border border-stone-600 text-white inline-flex items-center justify-center cursor-pointer hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>,
    document.body,
  );
}

/**
 * @param {(choice: {provider: string, level: string, slug: string}) => void} props.onSignup
 * @param {(choice: {provider: string, level: string, slug: string}) => void} props.onTryFree
 * @param {boolean} props.isAuthenticated  Signed-in visitors get "Go to dashboard" instead.
 */
export default function ExamPaperPicker({ onSignup, onTryFree, isAuthenticated = false }) {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [choice, setChoice] = useState({ provider: 'telc', level: 'B2' });
  const [phase, setPhase] = useState('shown'); // shown | leaving | arriving
  const [hover, setHover] = useState(-1);
  const [open, setOpen] = useState(-1);
  const timers = useRef([]);
  // The latest REQUESTED choice. A second click can land while the first switch
  // is still animating; it must build on what was asked for, not on what is shown.
  const target = useRef(choice);

  const slug = `${choice.provider}-${choice.level.toLowerCase()}`;
  const exam = getMockExam(slug);
  const providerName = PROVIDERS.find((p) => p.id === choice.provider)?.name;
  const short = `${providerName} ${choice.level}`;
  const labels = PAGE_LABELS[slug];
  const more = Math.max(0, (exam?.variants || 1) - 1);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // Leaving pages slide into the deck, the new booklet slides out of it.
  const pick = useCallback((change) => {
    const next = { ...target.current, ...change };
    if (next.provider === target.current.provider && next.level === target.current.level) return;
    target.current = next;
    timers.current.forEach(clearTimeout);
    if (reduced) {
      setChoice(next);
      return;
    }
    setHover(-1);
    setPhase('leaving');
    timers.current = [
      setTimeout(() => {
        setChoice(next);
        setPhase('arriving');
        timers.current.push(setTimeout(() => setPhase('shown'), 40));
      }, 300),
    ];
  }, [reduced]);

  const step = useCallback((d) => setOpen((i) => (i + d + 3) % 3), []);
  const close = useCallback(() => setOpen(-1), []);
  const payload = { provider: choice.provider, level: choice.level, slug };

  const pageStyle = (i) => {
    const p = REST[i];
    const lift = hover === i && phase === 'shown';
    let x = p.x, y = p.y, r = p.r, opacity = 1, delay = 0;
    let transition = 'transform .55s cubic-bezier(.2,.7,.2,1), opacity .35s ease, box-shadow .3s ease';
    if (phase === 'leaving') { x = DECK_X; y = 40; r = 4; opacity = 0; delay = (2 - i) * 50; transition = 'transform .3s ease-in, opacity .28s ease-in'; }
    if (phase === 'arriving') { x = DECK_X; y = 40; r = 4; opacity = 0; transition = 'none'; }
    if (phase === 'shown') delay = i * 90;
    if (lift) { y = p.y - 16; r = 0; delay = 0; }
    return {
      zIndex: lift ? 9 : p.z,
      opacity,
      transform: `translate(${x}px, ${y}px) rotate(${r}deg)`,
      transition,
      transitionDelay: `${delay}ms`,
      boxShadow: lift ? '0 40px 70px -28px rgba(28,25,23,.55)' : '0 20px 40px -24px rgba(28,25,23,.45), 0 1px 0 rgba(28,25,23,.06)',
    };
  };

  const signupLabel = isAuthenticated
    ? t('home.nav.goToDashboard')
    : t('homeB.paperPicker.signup', { exam: short, defaultValue: 'Sign up for {{exam}}' });

  const steps = (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center gap-2">
        <StepLabel n="1">{t('homeB.paperPicker.exam', 'Exam')}</StepLabel>
        <div role="group" aria-label={t('homeB.paperPicker.exam', 'Exam')} className="flex gap-1.5">
          {PROVIDERS.map((p) => (
            <Chip key={p.id} on={p.id === choice.provider} onClick={() => pick({ provider: p.id })}>{p.name}</Chip>
          ))}
        </div>
      </div>
      <span aria-hidden="true" className="hidden lg:block w-4 h-px bg-stone-300" />
      <div className="flex flex-col lg:flex-row lg:items-center gap-2">
        <StepLabel n="2">{t('homeB.paperPicker.level', 'Level')}</StepLabel>
        <div role="group" aria-label={t('homeB.paperPicker.level', 'Level')} className="flex gap-1.5">
          {LEVELS.map((lv) => (
            <Chip key={lv} level on={lv === choice.level} onClick={() => pick({ level: lv })}>{lv}</Chip>
          ))}
        </div>
      </div>
      <span aria-hidden="true" className="hidden lg:block w-4 h-px bg-stone-300" />
      <button
        type="button"
        onClick={() => onSignup(payload)}
        className="hidden lg:inline-flex shrink-0 whitespace-nowrap items-center gap-2 min-h-[48px] px-5 rounded-full bg-stone-900 text-white text-[15px] font-semibold cursor-pointer hover:bg-stone-800 active:scale-[0.97] transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-900"
        style={DISPLAY}
      >
        <span className="text-stone-400">3</span>
        {signupLabel}
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>
    </>
  );

  const deckCard = (
    <div className="relative w-[200px] lg:w-[250px] aspect-[250/354] shrink-0">
      <span aria-hidden="true" className="absolute inset-0 rounded bg-white border border-stone-200 translate-x-3.5 translate-y-3.5 rotate-[4deg]" />
      <span aria-hidden="true" className="absolute inset-0 rounded bg-white border border-stone-200 translate-x-[7px] translate-y-[7px] rotate-2" />
      <div className="absolute inset-0 flex flex-col justify-between p-5 lg:p-6 rounded bg-stone-900 text-white shadow-[0_22px_44px_-24px_rgba(28,25,23,.6)]">
        <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-stone-400">{exam?.examName}</span>
        <span className="text-[72px] lg:text-[88px] leading-[0.9] font-bold text-amber-200 tabular-nums" style={{ ...DISPLAY, letterSpacing: '-0.05em' }}>
          +{more}
        </span>
        <span className="text-[14px] lg:text-[15px] leading-snug text-stone-200">
          {t('homeB.paperPicker.more', { count: more, defaultValue: 'more full papers, each with different texts.' })}
        </span>
      </div>
    </div>
  );

  return (
    <section className="relative z-10 bg-stone-50 border-b border-stone-200 py-14 sm:py-20" aria-labelledby="paper-picker-heading">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5 xl:gap-8">
          <h2 id="paper-picker-heading" className="text-[2rem] sm:text-[2.5rem] leading-[1.04] font-bold tracking-tight text-stone-900 whitespace-nowrap" style={DISPLAY}>
            {t('homeB.paperPicker.headingLead', 'See the')}{' '}
            <span className="relative inline-block">
              {t('homeB.paperPicker.headingAccent', 'real paper.')}
              <svg viewBox="0 0 300 16" preserveAspectRatio="none" aria-hidden="true" className="absolute left-0 -bottom-1.5 w-full h-3.5 overflow-visible">
                <path d="M 4 11 C 40 5, 80 13, 130 7 C 180 3, 220 12, 260 6 C 275 5, 290 9, 296 8" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" opacity="0.55" />
              </svg>
            </span>
          </h2>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-1.5 lg:p-2 lg:rounded-full lg:bg-white lg:border lg:border-stone-200 lg:shadow-[0_16px_32px_-24px_rgba(28,25,23,.4)]">
            {steps}
          </div>
        </div>

        {/* Desktop: pages laid out wide, the deck at the end. Laid out for the 1104px
            xl content width and scaled down on smaller laptops so nothing overlaps. */}
        <div className="hidden lg:block relative h-[390px] xl:h-[440px] mt-8 w-[1104px] origin-top-left lg:scale-[0.88] xl:scale-100">
          {[0, 1, 2].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => setOpen(i)}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(-1)}
              aria-label={t('homeB.paperPicker.read', { page: labels[i], defaultValue: 'Read page: {{page}}' })}
              className="absolute left-0 top-0 w-[270px] h-[382px] p-0 border-0 rounded-[3px] bg-white cursor-zoom-in focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-stone-900"
              style={pageStyle(i)}
            >
              <img src={pageSrc(slug, i)} alt="" loading="lazy" decoding="async" className="block w-full h-full object-cover rounded-[3px]" />
            </button>
          ))}
          <div className="absolute right-0 top-[34px]">{deckCard}</div>
        </div>

        {/* Phone and tablet: swipe through the pages, the deck last. */}
        <div className="lg:hidden mt-6 -mx-4 sm:-mx-6 px-4 sm:px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4" style={{ scrollbarWidth: 'none' }}>
          {[0, 1, 2].map((i) => (
            <button
              key={`${slug}-${i}`}
              type="button"
              onClick={() => setOpen(i)}
              aria-label={t('homeB.paperPicker.read', { page: labels[i], defaultValue: 'Read page: {{page}}' })}
              className="snap-start shrink-0 w-[200px] aspect-[270/382] p-0 border-0 rounded-[3px] bg-white shadow-[0_16px_32px_-20px_rgba(28,25,23,.45)] cursor-zoom-in animate-fade-in focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-900"
            >
              <img src={pageSrc(slug, i)} alt="" loading="lazy" decoding="async" className="block w-full h-full object-cover rounded-[3px]" />
            </button>
          ))}
          <div className="snap-start pr-4">{deckCard}</div>
        </div>

        <div className="mt-2 lg:mt-0 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => onSignup(payload)}
            className="lg:hidden w-full inline-flex items-center justify-center gap-2 min-h-[52px] rounded-full bg-stone-900 text-white text-[16px] font-semibold cursor-pointer active:scale-[0.98] transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-900"
            style={DISPLAY}
          >
            {signupLabel}
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
          {!isAuthenticated && (
            <button
              type="button"
              onClick={() => onTryFree(payload)}
              className="min-h-[44px] text-[14px] font-semibold text-stone-500 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-900 cursor-pointer transition-colors duration-200"
            >
              {t('homeB.paperPicker.tryFree', { exam: short, defaultValue: 'or try {{exam}} free without an account' })}
            </button>
          )}
        </div>
      </div>

      {open >= 0 && (
        <PageDialog slug={slug} examName={exam?.examName} index={open} onClose={close} onStep={step} t={t} />
      )}
    </section>
  );
}
